import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useToast } from "@shared/hooks/use-toast";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";
import { axSystemTypesApi } from "./api";

export const useCreateSystemType = (searchData?: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  const config_level = "system_type";
  const show_inactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.createSystemType(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Created",
          description: "System type has been created successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            config_level,
            show_inactive
          ),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.searchSystemType(
            remoteUtilityId,
            "system_json-system_type",
            searchData
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateSystemType = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const config_level = "system_type";
  const show_inactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.updateSystemType(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Updated",
          description: "System type has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            config_level,
            show_inactive
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useCreateSystemTypeParameter = (codeList: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  const config_level = "system_parameter";
  const show_inactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.createSystemTypeParameter(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Parameter Created",
          description: "System type parameter has been created successfully.",
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemType.getAllSystemTypeParameter(
              remoteUtilityId,
              config_level,
              show_inactive,
              codeList
            ),
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            "system_type",
            show_inactive
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useUpdateSystemTypeParameter = (codeList: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  const configLevel = "system_parameter";
  const showInactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.updateSystemTypeParameter(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Parameter Updated",
          description: "System type parameter has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemType.getAllSystemTypeParameter(
              remoteUtilityId,
              configLevel,
              showInactive,
              codeList
            ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useGetSystemType = (
  configLevel: string,
  showInactive: boolean
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemType.getAllSystemType(
      remoteUtilityId,
      configLevel,
      showInactive
    ),
    () =>
      axSystemTypesApi.getSystemType(
        remoteUtilityId,
        configLevel,
        showInactive
      ),
    {
      enabled: !!remoteUtilityId,
    }
  );
};

export const useToggleSystemType = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const configLevel = "system_type";
  const showInactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.toggleSystemType(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Updated",
          description: "System type has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            configLevel,
            showInactive
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message, variant: "destructive" });
      },
    }
  );
};

export const useDeleteSystemTypeParameter = (codeList: string) => {
  const remoteUtilityId = getRemoteUtilityId();
  const configLevel = "system_parameter";
  const showInactive = true;
  const configLevelType = "system_type";
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.deleteSystemType(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Parameter Deleted",
          description: "System type parameter has been deleted successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            configLevelType,
            showInactive
          ),
        });
        queryClient.refetchQueries({
          queryKey:
            QueryKeyFactory.module.ax.systemType.getAllSystemTypeParameter(
              remoteUtilityId,
              configLevel,
              showInactive,
              codeList
            ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};

export const useSearchSystemType = (
  searchField: string,
  searchData: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemType.searchSystemType(
      remoteUtilityId,
      searchField,
      searchData
    ),
    () =>
      axSystemTypesApi.searchSystemType(
        remoteUtilityId,
        searchField,
        searchData
      ),
    {
      enabled: !!remoteUtilityId && !!searchData,
    }
  );
};

export const useGetSystemTypeParameter = (
  configLevel: string,
  showInactive: boolean,
  codeList: string
) => {
  const remoteUtilityId = getRemoteUtilityId();
  return useSmartQuery(
    QueryKeyFactory.module.ax.systemType.getAllSystemTypeParameter(
      remoteUtilityId,
      configLevel,
      showInactive,
      codeList
    ),
    () =>
      axSystemTypesApi.getSystemTypeParameter(
        remoteUtilityId,
        configLevel,
        showInactive,
        codeList
      ),
    {
      enabled: !!remoteUtilityId && !!codeList,
    }
  );
};

export const useUpdateSystemCriticality = () => {
  const remoteUtilityId = getRemoteUtilityId();
  const config_level = "system_type";
  const show_inactive = true;
  const { toast } = useToast();

  return useSmartMutation(
    (payload: any) => axSystemTypesApi.updateSystemType(payload),
    {
      onSuccess: (_data, payload) => {
        toast({
          title: "System Type Updated",
          description: "System type has been updated successfully.",
        });
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.ax.systemType.getAllSystemType(
            remoteUtilityId,
            config_level,
            show_inactive
          ),
        });
      },
      onError: (e) => {
        toast({ title: e?.message });
      },
    }
  );
};
